import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

interface TenantCreditsInfo {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  active: boolean;
  credits: {
    balance: number;
    updatedAt: string | null;
  };
}

interface CreditTransaction {
  id: string;
  type: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string | null;
  performedBy: string;
  performedByName: string | null;
  metadata: any;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const TYPE_LABELS: Record<string, string> = {
  ADD: 'Adição',
  REMOVE: 'Remoção',
  CONSUME: 'Consumo',
  REFUND: 'Reembolso',
  ADJUST: 'Ajuste',
  BONUS: 'Bônus',
};

const TYPE_COLORS: Record<string, string> = {
  ADD: 'bg-green-100 text-green-800',
  REMOVE: 'bg-red-100 text-red-800',
  CONSUME: 'bg-yellow-100 text-yellow-800',
  REFUND: 'bg-blue-100 text-blue-800',
  ADJUST: 'bg-purple-100 text-purple-800',
  BONUS: 'bg-emerald-100 text-emerald-800',
};

export function CreditsManager() {
  const [tenantCredits, setTenantCredits] = useState<TenantCreditsInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<TenantCreditsInfo | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'add' | 'remove' | 'set'>('add');
  const [modalTenant, setModalTenant] = useState<TenantCreditsInfo | null>(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [creditType, setCreditType] = useState<string>('ADD');
  const [submitting, setSubmitting] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return fetch(url, { ...options, headers });
  };

  const loadCredits = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch('/api/credits/admin/all');
      if (response.ok) {
        const data = await response.json();
        setTenantCredits(data.data || []);
      } else {
        toast.error('Erro ao carregar créditos');
      }
    } catch (error) {
      console.error('Erro ao carregar créditos:', error);
      toast.error('Erro ao carregar créditos');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async (tenantId: string, page = 1) => {
    setTransactionsLoading(true);
    try {
      const response = await authenticatedFetch(`/api/credits/admin/${tenantId}?page=${page}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.data.transactions || []);
        setPagination(data.data.pagination || null);
      }
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    loadCredits();
  }, []);

  const openModal = (tenant: TenantCreditsInfo, action: 'add' | 'remove' | 'set') => {
    setModalTenant(tenant);
    setModalAction(action);
    setAmount('');
    setDescription('');
    setCreditType(action === 'add' ? 'ADD' : 'REMOVE');
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!modalTenant) return;

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount < 0) {
      toast.error('Informe um valor válido');
      return;
    }

    if (modalAction !== 'set' && numAmount === 0) {
      toast.error('Informe um valor maior que zero');
      return;
    }

    setSubmitting(true);
    try {
      let url = '';
      let body: any = {};

      if (modalAction === 'add') {
        url = `/api/credits/admin/${modalTenant.tenantId}/add`;
        body = { amount: numAmount, description, type: creditType };
      } else if (modalAction === 'remove') {
        url = `/api/credits/admin/${modalTenant.tenantId}/remove`;
        body = { amount: numAmount, description };
      } else {
        url = `/api/credits/admin/${modalTenant.tenantId}/set`;
        body = { balance: numAmount, description };
      }

      const response = await authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Operação realizada com sucesso');
        setIsModalOpen(false);
        loadCredits();
        if (selectedTenant?.tenantId === modalTenant.tenantId) {
          loadTransactions(modalTenant.tenantId);
        }
      } else {
        toast.error(data.message || 'Erro na operação');
      }
    } catch (error) {
      console.error('Erro na operação de créditos:', error);
      toast.error('Erro na operação de créditos');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectTenant = (tenant: TenantCreditsInfo) => {
    if (selectedTenant?.tenantId === tenant.tenantId) {
      setSelectedTenant(null);
      setTransactions([]);
      setPagination(null);
    } else {
      setSelectedTenant(tenant);
      loadTransactions(tenant.tenantId);
    }
  };

  const filteredTenants = tenantCredits.filter(t =>
    t.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.tenantSlug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatInteger = (num: number) => {
    return num.toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Carregando créditos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
          <p className="text-sm opacity-80">Total de Empresas</p>
          <p className="text-2xl font-bold">{formatInteger(tenantCredits.length)}</p>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
          <p className="text-sm opacity-80">Créditos Totais Distribuídos</p>
          <p className="text-2xl font-bold">
            {formatNumber(tenantCredits.reduce((sum, t) => sum + t.credits.balance, 0))}
          </p>
        </div>
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg p-4 text-white">
          <p className="text-sm opacity-80">Empresas sem Créditos</p>
          <p className="text-2xl font-bold">
            {formatInteger(tenantCredits.filter(t => t.credits.balance === 0).length)}
          </p>
        </div>
      </div>

      {/* Busca */}
      <div className="relative">
        <input
          type="text"
          placeholder="Buscar empresa por nome ou slug..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {/* Lista de tenants com créditos */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Créditos por Empresa</h3>
        </div>

        {filteredTenants.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchQuery ? 'Nenhuma empresa encontrada para esta busca.' : 'Nenhuma empresa cadastrada.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredTenants.map((tenant) => (
              <div key={tenant.tenantId}>
                <div className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => handleSelectTenant(tenant)}
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <h4 className="text-base font-medium text-gray-900">{tenant.tenantName}</h4>
                          <p className="text-sm text-gray-500 font-mono">{tenant.tenantSlug}</p>
                        </div>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                          tenant.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {tenant.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Saldo */}
                      <div className="text-right mr-4">
                        <p className="text-xs text-gray-500">Saldo</p>
                        <p className={`text-xl font-bold ${
                          tenant.credits.balance > 0 ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {formatNumber(tenant.credits.balance)}
                        </p>
                      </div>

                      {/* Ações */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => openModal(tenant, 'add')}
                          className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                          title="Adicionar créditos"
                        >
                          + Adicionar
                        </button>
                        <button
                          onClick={() => openModal(tenant, 'remove')}
                          className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                          title="Remover créditos"
                        >
                          - Remover
                        </button>
                        <button
                          onClick={() => openModal(tenant, 'set')}
                          className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                          title="Definir saldo"
                        >
                          = Definir
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Histórico de transações expandido */}
                {selectedTenant?.tenantId === tenant.tenantId && (
                  <div className="bg-gray-50 border-t border-gray-200 px-6 py-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Histórico de Transações - {tenant.tenantName}
                    </h4>

                    {transactionsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Carregando...</span>
                      </div>
                    ) : transactions.length === 0 ? (
                      <p className="text-sm text-gray-500 py-4 text-center">Nenhuma transação registrada.</p>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-300">
                                <th className="text-left py-2 px-3 font-medium text-gray-600">Data</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-600">Tipo</th>
                                <th className="text-right py-2 px-3 font-medium text-gray-600">Quantidade</th>
                                <th className="text-right py-2 px-3 font-medium text-gray-600">Saldo Antes</th>
                                <th className="text-right py-2 px-3 font-medium text-gray-600">Saldo Depois</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-600">Descrição</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-600">Realizado por</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transactions.map((tx) => (
                                <tr key={tx.id} className="border-b border-gray-200 hover:bg-white">
                                  <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                                    {formatDate(tx.createdAt)}
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                      TYPE_COLORS[tx.type] || 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {TYPE_LABELS[tx.type] || tx.type}
                                    </span>
                                  </td>
                                  <td className={`py-2 px-3 text-right font-medium ${
                                    tx.amount > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {tx.amount > 0 ? '+' : ''}{formatNumber(tx.amount)}
                                  </td>
                                  <td className="py-2 px-3 text-right text-gray-500">
                                    {formatNumber(tx.balanceBefore)}
                                  </td>
                                  <td className="py-2 px-3 text-right font-medium text-gray-900">
                                    {formatNumber(tx.balanceAfter)}
                                  </td>
                                  <td className="py-2 px-3 text-gray-600 max-w-xs truncate">
                                    {tx.description || '-'}
                                  </td>
                                  <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                                    {tx.performedByName || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Paginação */}
                        {pagination && pagination.totalPages > 1 && (
                          <div className="flex items-center justify-between mt-4">
                            <p className="text-xs text-gray-500">
                              Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                              {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                              {pagination.total} transações
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => loadTransactions(tenant.tenantId, pagination.page - 1)}
                                disabled={pagination.page <= 1}
                                className="px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Anterior
                              </button>
                              <span className="px-3 py-1 text-xs text-gray-600">
                                {pagination.page} / {pagination.totalPages}
                              </span>
                              <button
                                onClick={() => loadTransactions(tenant.tenantId, pagination.page + 1)}
                                disabled={pagination.page >= pagination.totalPages}
                                className="px-3 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Próxima
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de operação */}
      {isModalOpen && modalTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalAction === 'add' && 'Adicionar Créditos'}
                {modalAction === 'remove' && 'Remover Créditos'}
                {modalAction === 'set' && 'Definir Saldo de Créditos'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Empresa: <span className="font-medium">{modalTenant.tenantName}</span>
              </p>
              <p className="text-sm text-gray-500">
                Saldo atual: <span className="font-bold text-gray-700">{formatNumber(modalTenant.credits.balance)}</span>
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Tipo (apenas para adição) */}
              {modalAction === 'add' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Adição
                  </label>
                  <select
                    value={creditType}
                    onChange={(e) => setCreditType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ADD">Adição Normal</option>
                    <option value="BONUS">Bônus</option>
                    <option value="REFUND">Reembolso</option>
                  </select>
                </div>
              )}

              {/* Quantidade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {modalAction === 'set' ? 'Novo Saldo' : 'Quantidade'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={modalAction === 'set' ? 'Ex: 1000' : 'Ex: 500'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                {modalAction === 'remove' && amount && parseFloat(amount) > modalTenant.credits.balance && (
                  <p className="text-red-500 text-xs mt-1">
                    Valor excede o saldo atual ({formatNumber(modalTenant.credits.balance)})
                  </p>
                )}
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Motivo da operação..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Preview */}
              {amount && !isNaN(parseFloat(amount)) && (
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-xs text-gray-500 mb-1">Resultado da operação:</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{formatNumber(modalTenant.credits.balance)}</span>
                    <span className="text-gray-400">→</span>
                    <span className={`text-sm font-bold ${
                      modalAction === 'add'
                        ? 'text-green-600'
                        : modalAction === 'remove'
                        ? 'text-red-600'
                        : 'text-purple-600'
                    }`}>
                      {modalAction === 'add' && formatNumber(modalTenant.credits.balance + parseFloat(amount))}
                      {modalAction === 'remove' && formatNumber(Math.max(0, modalTenant.credits.balance - parseFloat(amount)))}
                      {modalAction === 'set' && formatNumber(parseFloat(amount))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !amount || isNaN(parseFloat(amount))}
                className={`flex-1 px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  modalAction === 'add'
                    ? 'bg-green-600 hover:bg-green-700'
                    : modalAction === 'remove'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {submitting ? 'Processando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
